import { useState } from 'react';
import type { MintedKey } from '@xinfer/api-client';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CopyButton,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@xinfer/ui';
import { ShieldAlert } from 'lucide-react';

/**
 * The one-time key reveal.
 *
 * This is the only moment the plaintext exists anywhere outside the user's
 * clipboard, so it is a hard modal: no toast, no auto-dismiss, and no way out
 * except an explicit acknowledgement. Getting this wrong is a support burden
 * forever, because the answer to "I lost my key" is always "mint a new one".
 *
 * Radix supplies the focus trap and the restoration of focus on close. What
 * this adds on top is the refusal to close: escape, the overlay click and the
 * corner X are all disabled until the checkbox is ticked, so no reflex
 * dismisses this dialog.
 */
export function KeyRevealModal({ minted, onClose }: { minted: MintedKey; onClose: () => void }) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Dialog open onOpenChange={(open) => !open && acknowledged && onClose()}>
      <DialogContent
        hideClose
        className="max-w-xl"
        onEscapeKeyDown={(event) => {
          if (!acknowledged) event.preventDefault();
        }}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Your new API key</DialogTitle>
          <DialogDescription>
            Use it as the bearer token against the inference endpoint.
          </DialogDescription>
        </DialogHeader>

        <Alert variant="warning">
          <ShieldAlert />
          <div>
            <AlertTitle>This is the only time this key is shown</AlertTitle>
            <AlertDescription>
              Only a hash of it is stored, so it cannot be recovered or shown again. If it is
              lost, the only remedy is to revoke this one and create another.
            </AlertDescription>
          </div>
        </Alert>

        <div className="rounded-lg border border-border bg-muted/50 p-3">
          <code
            className="block break-all font-mono text-sm leading-relaxed"
            data-testid="plaintext-key"
          >
            {minted.key}
          </code>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            Ends in <span className="font-mono">{minted.hint}</span>
          </span>
          <CopyButton value={minted.key} label="Copy key" variant="outline" />
        </div>

        <label className="flex items-start gap-2.5 rounded-md border border-border p-3 text-sm">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 size-4 accent-[var(--primary)]"
          />
          <span>I have stored this key somewhere safe</span>
        </label>

        <DialogFooter>
          <Button disabled={!acknowledged} onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
